import React from 'react';
import {
  FiCheckSquare,
  FiBriefcase,
  FiMessageCircle,
  FiFolder,
  FiKey,
  FiCamera,
} from 'react-icons/fi';
import { HomeBadge, HomeBtn, HomeChecks, HomeKicker } from './HomePrimitives';
import { HomeReveal } from './HomeMotion';
import HomeTaskVisual from './HomeTaskVisual';
import HomeProjectVisual from './HomeProjectVisual';
import HomeForumVisual from './HomeForumVisual';
import HomeSnapBillVisual from './HomeSnapBillVisual';
import HomeDocumentsVisual from './HomeDocumentsVisual';
import HomeVaultVisual from './HomeVaultVisual';

const HomeModules = () => (
  <div id="modules">
    <ModuleBlock
      badgeIcon={<FiCheckSquare />}
      badge="Task Management"
      title="Organize, assign, and track every task in one place"
      copy="flowdo helps your team stay organized, manage daily work, meet deadlines, and focus on what matters most with complete visibility and control."
      items={[
        'Create and assign tasks easily',
        'Track deadlines and priorities',
        'Collaborate with comments and files',
        'Monitor progress in real time',
      ]}
      to="/products/task"
      visualNode={<HomeTaskVisual />}
      kicker
      warm
    />
    <ModuleBlock
      badgeIcon={<FiCamera />}
      badge="Snap Bill"
      title="AI driven bills processing"
      copy="Snap Bill extracts expense bills and pushes to your accounting platform, avoiding data entry time. Generate LPO from supplier estimates and compare SOA using AI."
      items={[
        'Capture Scanned bill data',
        'Auto push data to existing accounting platform',
        'No setup or updates required',
        'Convert Estimates to LPO',
        'Auto verify SOA',
      ]}
      to="/products/snap-bill"
      visualNode={<HomeSnapBillVisual />}
      reverse
      sectionClass="home-module--snap"
    />
    <ModuleBlock
      badgeIcon={<FiFolder />}
      badge="Documents"
      title="Store, share, and organize business documents securely"
      copy="flowdo Documents helps your team keep important files organized, accessible, and secure—so you can collaborate with confidence. Get reminders based on document expiry details."
      items={[
        'Upload any type of documents into various classifications',
        'Stored and displayed securely',
        'Share with select team members',
        'Get reminders based on expiry date',
        'Automatic Version control',
      ]}
      to="/products/documents"
      visualNode={<HomeDocumentsVisual />}
      sectionClass="home-module--docs"
    />
    <ModuleBlock
      badgeIcon={<FiBriefcase />}
      badge="Projects"
      title="Plan projects end-to-end and keep every milestone on track"
      copy="flowdo project management helps you plan, collaborate, and deliver projects on time and within budget—while keeping your team and stakeholders aligned every step of the way."
      items={[
        'Create projects with clear owners',
        'Set timelines and priorities',
        'Track progress and delays',
        'Share updates with stakeholders',
      ]}
      to="/products/projects"
      visualNode={<HomeProjectVisual />}
      reverse
      warm
      sectionClass="home-module--projects"
    />
    <ModuleBlock
      badgeIcon={<FiKey />}
      badge="Credentials Vault"
      title="Keep corporate and personal credentials safe in one vault"
      copy="flowdo Credentials Vault securely stores and manages passwords, API keys, and sensitive information—so your team stays protected and productive."
      items={[
        '2-level secured storage',
        'Store credentials securely',
        'Separate corporate and personal vaults',
        'Share access with roles',
        'Audit who viewed what',
        'Reduce password sprawl',
      ]}
      to="/products/credentials-vault"
      visualNode={<HomeVaultVisual />}
      sectionClass="home-module--vault"
    />
    <ModuleBlock
      badgeIcon={<FiMessageCircle />}
      badge="Discussion Forum"
      title="Centralize team conversations and decisions in one place"
      copy="flowdo's Discussion Forum brings all conversations together—so your team stays aligned, informed, and ready to move forward."
      items={[
        'Start topic-based discussions',
        'Share files and updates',
        'Keep context with replies',
        'Notify the right people',
        'Search past conversations easily',
      ]}
      to="/products/discussion-forum"
      visualNode={<HomeForumVisual />}
      reverse
      sectionClass="home-module--forum"
    />
  </div>
);

function ModuleBlock({
  badgeIcon,
  badge,
  title,
  copy,
  items,
  to,
  visual,
  visualAlt,
  visualNode,
  reverse = false,
  warm = false,
  kicker = false,
  sectionClass = '',
}) {
  const gridClass = [
    'home-container',
    'home-module-grid',
    reverse ? 'home-module-grid--reverse' : '',
  ].filter(Boolean).join(' ');

  return (
    <section className={`home-section home-module ${warm ? 'home-module--warm' : ''}${kicker ? ' home-module--tasks' : ''}${sectionClass ? ` ${sectionClass}` : ''}`}>
      <div className={gridClass}>
        <HomeReveal className="home-module-copy-col" from={reverse ? 'right' : 'left'}>
          <div className="home-module-intro">
            {kicker ? (
              <HomeKicker icon={badgeIcon}>{badge}</HomeKicker>
            ) : (
              <HomeBadge icon={badgeIcon}>{badge}</HomeBadge>
            )}
            <h1 className="home-module-title">{title}</h1>
          </div>
          <div className="home-module-body">
            <p className="home-module-copy">{copy}</p>
            <HomeChecks items={items} />
            <HomeBtn to={to}>Learn More</HomeBtn>
          </div>
        </HomeReveal>
        <div className="home-visual">
          {visualNode || (
            <HomeReveal from={reverse ? 'left' : 'right'}>
              <img src={visual} alt={visualAlt} className="home-module-photo" />
            </HomeReveal>
          )}
        </div>
      </div>
    </section>
  );
}

export default HomeModules;
